import { useEffect, useRef, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";

interface ClickAMoleProps {
  containerRef: React.RefObject<HTMLElement | null>;
  onEnd: () => void;
}

const LOGO_SRC = "/favicon-excelfore.ico";

export default function ClickAMole({ containerRef, onEnd }: ClickAMoleProps) {
  const [score, setScore] = useState(0);
  const [moleStyle, setMoleStyle] = useState<React.CSSProperties>({ display: "none" });
  const [visible, setVisible] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const scheduleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const activeRef = useRef(true);

  const getContainers = useCallback(() => {
    const container = containerRef.current;
    if (!container) return [];
    return Array.from(
      container.querySelectorAll<HTMLElement>(
        "[class*='MuiCard'], [class*='MuiPaper'], [class*='MuiBox']",
      ),
    ).filter((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 100 && r.height > 60;
    });
  }, [containerRef]);

  const showMole = useCallback(() => {
    if (!activeRef.current) return;

    const containers = getContainers();
    if (containers.length === 0) return;

    const target = containers[Math.floor(Math.random() * containers.length)];
    const rect = target.getBoundingClientRect();

    // Pop out from a random edge of the container
    const edge = Math.floor(Math.random() * 4); // 0=top, 1=right, 2=bottom, 3=left
    let left = rect.left + rect.width / 2 - 40;
    let top = rect.top + rect.height / 2 - 40;

    if (edge === 0) top = rect.top - 40;
    else if (edge === 1) left = rect.right - 40;
    else if (edge === 2) top = rect.bottom - 40;
    else left = rect.left - 40;

    setMoleStyle({
      position: "fixed",
      left: `${left}px`,
      top: `${top}px`,
      width: "80px",
      height: "80px",
      zIndex: 3000,
      cursor: "pointer",
      animation: "mole_pop 0.25s cubic-bezier(0.34,1.56,0.64,1) forwards",
      filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.5))",
      borderRadius: "50%",
      background: "rgba(255,255,255,0.9)",
      padding: "8px",
      boxSizing: "border-box",
    });
    setVisible(true);

    // Auto-hide after 1 second if not clicked
    hideTimerRef.current = setTimeout(() => {
      hideMole();
      scheduleNext();
    }, 1000);
  }, [getContainers]);

  const hideMole = useCallback(() => {
    setVisible(false);
    setMoleStyle({ display: "none" });
    clearTimeout(hideTimerRef.current);
  }, []);

  const scheduleNext = useCallback(() => {
    if (!activeRef.current) return;
    const delay = 800 + Math.random() * 1200;
    scheduleTimerRef.current = setTimeout(showMole, delay);
  }, [showMole]);

  const handleClick = useCallback(() => {
    clearTimeout(hideTimerRef.current);
    setScore((s) => s + 1);
    hideMole();
    scheduleNext();
  }, [hideMole, scheduleNext]);

  useEffect(() => {
    // Inject mole pop animation
    const style = document.createElement("style");
    style.textContent = `
      @keyframes mole_pop {
        from { transform: scale(0) rotate(-20deg); opacity: 0; }
        to   { transform: scale(1) rotate(0deg);  opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    // Start the game
    scheduleNext();

    return () => {
      activeRef.current = false;
      clearTimeout(hideTimerRef.current);
      clearTimeout(scheduleTimerRef.current);
      style.remove();
    };
  }, []);

  return (
    <>
      {/* Score overlay */}
      <Box
        sx={{
          position: "fixed",
          top: 80,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 3100,
          background: "rgba(0,0,0,0.75)",
          color: "#fff",
          px: 3,
          py: 1.5,
          borderRadius: 4,
          textAlign: "center",
          backdropFilter: "blur(8px)",
          pointerEvents: "none",
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          🎯 {score}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.6 }}>
          Click the logo! · Double-ESC to quit
        </Typography>
      </Box>

      {/* Mole logo */}
      {visible && (
        <img
          src={LOGO_SRC}
          alt="mole"
          onClick={handleClick}
          style={moleStyle as React.CSSProperties}
        />
      )}
    </>
  );
}
